// Workspace home view styles: capability cards, feed, command additions and
// the task-creation composer (WEB-8003). Composed by workspaceStyles.ts at the
// sections' original positions — source order is part of the cascade.
export const homeCards = `
/* ===== Home cards (capability quartet) ===== */
.ws-chat-starters:has(.ws-home-cards) {
  container-type: inline-size;
}
.ws-home-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: 100%;
}
@container (max-width: 640px) {
  .ws-home-cards { grid-template-columns: 1fr; }
}
.ws-home-card {
  --ws-home-tint: var(--ws-ink-3);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px 14px;
  background: var(--ws-panel);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  text-align: left;
  font-family: var(--ws-sans);
  color: inherit;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease, background 0.18s ease;
}
.ws-home-card:hover {
  border-color: color-mix(in srgb, var(--ws-home-tint) 45%, var(--ws-line));
  background: color-mix(in srgb, var(--ws-home-tint) 2%, var(--ws-panel));
}
.ws-home-card:focus-visible {
  outline: none;
  border-color: var(--ws-home-tint);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--ws-home-tint) 18%, transparent);
}
.ws-home-card-head {
  display: flex;
  align-items: center;
  gap: 11px;
}
.ws-home-card-icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  color: var(--ws-home-tint);
  background: color-mix(in srgb, var(--ws-home-tint) 10%, transparent);
  flex-shrink: 0;
}
.ws-home-card-titles {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.ws-home-card-title {
  font-family: var(--ws-serif);
  font-size: 18px;
  font-weight: 500;
  line-height: 1.15;
  color: var(--ws-ink);
  letter-spacing: -0.005em;
}
.ws-home-card-tagline {
  font-size: 12px;
  font-style: italic;
  color: var(--ws-ink-4);
  line-height: 1.3;
}
.ws-home-card-body {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ws-ink-3);
}
.ws-home-card-examples {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.ws-home-card-examples li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--ws-ink-3);
}
.ws-home-card-examples li svg {
  color: var(--ws-home-tint);
  opacity: 0.85;
  flex-shrink: 0;
}
/* Short viewports: trim the quartet so all four cards + the composer fit
   without the page looking cramped. Drops the examples and tightens spacing,
   mirroring the latest-preview height guard below. */
@media (max-height: 860px) {
  .ws-home-card { padding: 13px 16px 11px; gap: 7px; }
  .ws-home-card-examples { display: none; }
  .ws-home-card-title { font-size: 16px; }
  .ws-home-card-body { font-size: 12px; }
  .ws-home-card-cta { padding-top: 6px; }
}
.ws-home-card-latest {
  padding-top: 10px;
  border-top: 1px solid var(--ws-line);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
@media (max-height: 900px) {
  .ws-home-card-latest { display: none; }
}
.ws-home-card-latest-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ws-ink-5);
}
.ws-home-card-latest-row {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.ws-home-card-latest-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--ws-ink-5);
}
.ws-home-card-latest-dot.ws-home-card-latest-ok { background: var(--ws-ok); }
.ws-home-card-latest-dot.ws-home-card-latest-warn { background: var(--ws-warn); }
.ws-home-card-latest-dot.ws-home-card-latest-mute { background: var(--ws-ink-5); }
.ws-home-card-latest-title {
  font-size: 12.5px;
  color: var(--ws-ink-2);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ws-home-card-latest-status {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ws-ink-5);
  flex-shrink: 0;
}
.ws-home-card-latest-status.ws-home-card-latest-ok { color: var(--ws-ok); }
.ws-home-card-latest-status.ws-home-card-latest-warn { color: var(--ws-warn); }
.ws-home-card-cta {
  margin-top: auto;
  padding-top: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ws-home-tint);
  letter-spacing: 0.005em;
}
.ws-home-card-cta svg {
  transition: transform 0.18s ease;
}
.ws-home-card:hover .ws-home-card-cta svg {
  transform: translateX(2px);
}
`;

export const homeViewAndFeed = `
/* ===== Home view ===== */
.ws-home {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.ws-home-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 32px 16px;
}
.ws-home-bottom {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 32px 24px;
}
.ws-home-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
}
.ws-home-hero h1 {
  font-family: var(--ws-sans);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--ws-ink);
}
.ws-chat-agent-picker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px 3px 4px;
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  background: var(--ws-panel);
  color: var(--ws-ink);
  font-family: var(--ws-sans);
  font-size: 12px;
  cursor: pointer;
}
.ws-chat-agent-picker:hover { background: var(--ws-hover); }

/* ===== Home feed ===== */
.ws-feed {
  width: 100%;
  max-width: 680px;
  margin-bottom: 28px;
}
.ws-feed-head {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ws-ink-4);
  margin-bottom: 10px;
}
.ws-feed-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  margin-bottom: 6px;
  cursor: pointer;
  background: var(--ws-panel);
  width: 100%;
  text-align: left;
  font-family: var(--ws-sans);
  transition: border-color 0.15s, background 0.15s;
}
.ws-feed-item:hover {
  border-color: var(--ws-line-2);
  background: white;
}
.ws-feed-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--ws-ink-3);
}
.ws-feed-icon-sched { background: rgba(74,107,133,0.1); color: var(--ws-a-forecasting); }
.ws-feed-icon-hook { background: rgba(95,127,106,0.1); color: var(--ws-a-prospecting); }
.ws-feed-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-feed-label {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ws-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.ws-feed-time {
  font-size: 12px;
  color: var(--ws-ink-4);
  flex-shrink: 0;
}
`;

export const homeCommandAdditions = `
/* ===== Command home additions ===== */
.ws-cmd-section { max-width: 880px; width: 100%; margin: 0 auto 18px; padding: 0 4px; }
.ws-cmd-section-head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--ws-ink-4); margin: 0 0 10px;
}
.ws-cmd-section-link {
  background: transparent; border: 0; padding: 0; cursor: pointer;
  font-size: 11px; color: var(--ws-ink-3); text-decoration: underline;
}
.ws-cmd-needs-you { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
.ws-cmd-needs-card {
  background: var(--ws-panel); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md); padding: 12px; cursor: pointer;
  text-align: left;
}
.ws-cmd-needs-card:hover { background: var(--ws-hover); }
.ws-cmd-needs-card-head {
  font-size: 11px; font-weight: 600; color: var(--ws-warn);
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px;
}
.ws-cmd-needs-card-title { font-size: 13.5px; line-height: 1.4; color: var(--ws-ink); }
.ws-cmd-progress-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px; border-bottom: 1px solid var(--ws-line); cursor: pointer;
}
.ws-cmd-progress-row:hover { background: var(--ws-hover); }
.ws-cmd-progress-spinner {
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid var(--ws-line-2);
  border-top-color: var(--ws-info);
  animation: ws-spin 0.9s linear infinite;
  flex-shrink: 0;
}
@keyframes ws-spin { to { transform: rotate(360deg); } }
.ws-cmd-progress-meta { font-size: 12px; color: var(--ws-ink-4); }
.ws-cmd-recent-row {
  display: grid; grid-template-columns: 1fr auto auto;
  align-items: center; gap: 10px; padding: 8px;
  border-bottom: 1px solid var(--ws-line); cursor: pointer;
}
.ws-cmd-recent-row:hover { background: var(--ws-hover); }
.ws-cmd-recent-events {
  background: var(--ws-soft); border-radius: var(--ws-r-sm);
  padding: 12px; margin: 0 0 8px;
}
.ws-cmd-continue-link {
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent; border: 0; padding: 4px 0;
  font-size: 12px; font-weight: 450; letter-spacing: 0; text-transform: none;
  color: var(--ws-ink-3); cursor: pointer;
  text-decoration: underline;
}
.ws-cmd-continue-link:hover { color: var(--ws-ink); }
`;

export const homeComposer = `
/* ===== Home composer (task creation) ===== */
.ws-home-composer {
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
}

/* Continue-last link above the new chat composer */
.ws-chat-continue-bar {
  display: flex;
  justify-content: flex-start;
  padding: 0 0 6px;
}

/* Analytics view */
.ws-tq-analytics { display: flex; flex-direction: column; gap: 16px; }
.ws-tq-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.ws-tq-stat {
  background: var(--ws-panel); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md); padding: 16px 18px;
}
.ws-tq-stat-k {
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--ws-ink-4);
}
.ws-tq-stat-v {
  margin-top: 6px; font-size: 32px; font-weight: 600; color: var(--ws-ink);
  font-variant-numeric: tabular-nums; line-height: 1; letter-spacing: -0.02em;
}
.ws-tq-unit { font-size: 18px; color: var(--ws-ink-3); font-weight: 500; margin-left: 2px; }
.ws-tq-stat-trend { margin-top: 8px; font-size: 11.5px; color: var(--ws-ink-3); }

/* Bar chart card */
.ws-tq-chart-card {
  background: var(--ws-panel); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md); padding: 18px 20px;
}
.ws-tq-chart-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 18px;
}
.ws-tq-chart-title { font-size: 14px; font-weight: 600; color: var(--ws-ink); }
.ws-tq-chart-sub { font-size: 11.5px; color: var(--ws-ink-3); margin-top: 2px; }
.ws-tq-chart-legend { display: flex; gap: 12px; font-size: 11px; color: var(--ws-ink-3); }
.ws-tq-chart-legend span { display: inline-flex; align-items: center; gap: 5px; }
.ws-tq-leg {
  width: 8px; height: 8px; border-radius: 2px; display: inline-block; font-style: normal;
}
.ws-tq-leg.done { background: var(--ws-ink); }
.ws-tq-leg.failed { background: #C9462C; }
.ws-tq-bars {
  display: flex; align-items: flex-end; gap: 8px;
  height: 110px; padding-bottom: 24px; position: relative;
}
.ws-tq-bar-wrap {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px;
  height: 100%; position: relative;
}
.ws-tq-bar-stack {
  display: flex; flex-direction: column-reverse;
  width: 100%; max-width: 32px; height: 100%;
  justify-content: flex-start; align-items: center; gap: 1px;
}
.ws-tq-bar {
  width: 100%; background: var(--ws-ink); border-radius: 3px 3px 0 0;
  transition: opacity 150ms;
}
.ws-tq-bar.fail { background: #C9462C; }
.ws-tq-bar-wrap:hover .ws-tq-bar { opacity: 0.75; }
.ws-tq-bar-label {
  position: absolute; bottom: 0;
  font-size: 10px; color: var(--ws-ink-4); font-variant-numeric: tabular-nums;
}

/* Agent breakdown */
.ws-tq-agent-breakdown {
  background: var(--ws-panel); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md); padding: 18px 20px;
}
.ws-tq-agent-row {
  display: grid; grid-template-columns: 22px 140px 1fr 50px 110px;
  gap: 12px; align-items: center; padding: 8px 0; font-size: 13px;
}
.ws-tq-agent-row + .ws-tq-agent-row { border-top: 1px solid var(--ws-line); }
.ws-tq-agent-sw {
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: white;
}
.ws-tq-agent-nm { color: var(--ws-ink); font-weight: 500; }
.ws-tq-agent-bar {
  height: 6px; background: var(--ws-soft); border-radius: 3px; overflow: hidden;
}
.ws-tq-agent-bar > span {
  display: block; height: 100%; border-radius: 3px; transition: width 0.2s;
}
.ws-tq-agent-count {
  text-align: right; font-variant-numeric: tabular-nums;
  font-size: 12px; color: var(--ws-ink-2);
}
.ws-tq-agent-cost {
  text-align: right; font-variant-numeric: tabular-nums;
  font-size: 12px; color: var(--ws-ink-4);
}
`;
